import type { UpdateSchedulingConfigurationInput } from 'app/generated/graphql'
import type { CustomQuestionType, LeadOverrideConfigType, SchedulingConfigurationJson } from 'app/types/entities'

// The shape both scheduler settings pages select from `schedulingConfiguration`.
// Typed structurally so either page's generated query type satisfies it.
export type SchedulingConfigurationSelection = {
  id: string
  locationId: string
  allowDoubleBooking?: boolean | null
  insurancesAccepted?: string[] | null
  insurancesRejected?: string[] | null
  isStateInsuranceAccepted?: boolean | null
  granularity: number
  customQuestions?: Array<{
    id: string
    fieldName: string
    required: boolean
    fieldType: string
    enabled: boolean
    description?: string | null
    marketingConsent?: boolean | null
    optIn?: boolean | null
  }> | null
  leadOverrideEnabled?: boolean | null
  leadOverrideConfig?: { amount: number; unit: string } | null
}

// GraphQL returns `null` for an unset field; the page state and the form components
// expect `undefined`, so the boundary normalizes once here.
export function toSchedulingConfigurationJson(sc: SchedulingConfigurationSelection): SchedulingConfigurationJson {
  return {
    id: sc.id,
    locationId: sc.locationId,
    allowDoubleBooking: sc.allowDoubleBooking ?? undefined,
    insurancesAccepted: sc.insurancesAccepted ?? undefined,
    insurancesRejected: sc.insurancesRejected ?? undefined,
    isStateInsuranceAccepted: sc.isStateInsuranceAccepted ?? undefined,
    granularity: sc.granularity,
    customQuestions: (sc.customQuestions ?? []).map((q) => ({
      id: q.id,
      fieldName: q.fieldName,
      required: q.required,
      fieldType: q.fieldType as CustomQuestionType[`fieldType`],
      enabled: q.enabled,
      description: q.description ?? undefined,
      marketingConsent: q.marketingConsent ?? undefined,
      optIn: q.optIn ?? undefined,
    })),
    leadOverrideEnabled: sc.leadOverrideEnabled ?? undefined,
    leadOverrideConfig: sc.leadOverrideConfig
      ? { amount: sc.leadOverrideConfig.amount, unit: sc.leadOverrideConfig.unit as LeadOverrideConfigType[`unit`] }
      : undefined,
  }
}

// Builds the update input from the page state. `marketingConsent` is the deprecated
// spelling of `optIn`, so a legacy question keeps its consent flag on save.
export function toSchedulingConfigurationInput(
  sc: SchedulingConfigurationJson | undefined,
  customQuestions: CustomQuestionType[],
): UpdateSchedulingConfigurationInput {
  return {
    allowDoubleBooking: sc?.allowDoubleBooking,
    insurancesAccepted: sc?.insurancesAccepted,
    insurancesRejected: sc?.insurancesRejected,
    isStateInsuranceAccepted: sc?.isStateInsuranceAccepted,
    granularity: sc?.granularity,
    leadOverrideEnabled: sc?.leadOverrideEnabled,
    leadOverrideConfig: sc?.leadOverrideConfig,
    customQuestions: customQuestions.map((q) => ({
      id: q.id,
      fieldName: q.fieldName,
      required: q.required,
      fieldType: q.fieldType,
      enabled: q.enabled,
      description: q.description,
      optIn: q.optIn ?? q.marketingConsent,
    })),
  }
}
